<!-- EditableTable.vue -->
<template>
  <div class="editable-table">
    <table v-if="isOpen">
      <thead>
        <tr>
          <th
            v-for="col in columns"
            :key="col.field"
            :class="getColumnAlignment(col)"
          >
            {{ col.label }}
          </th>

          <th class="align-center">
            Actions
          </th>
        </tr>
      </thead>

      <tbody v-if="rows.length">
        <tr
          v-for="(row, rowIndex) in rows"
          :key="row._rowId"
        >
          <td
            v-for="col in columns"
            :key="col.field"
            :class="getColumnAlignment(col)"
          >
            <!-- TEXT -->
            <template v-if="col.type === 'text'">
              <input
                v-if="row.editing"
                type="text"
                v-model="row[col.field]"
                :placeholder="col.placeholder || ''"
                @input="emitIfAutoSave"
              />

              <span v-else>
                {{ row[col.field] }}
              </span>
            </template>

            <!-- NUMBER -->
            <template v-else-if="col.type === 'number'">
              <input
                v-if="row.editing"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                :value="row[col.field]"
                :placeholder="col.placeholder || ''"
                @input="onNumberInput(row, col.field, $event)"
              />

              <span v-else>
                {{ row[col.field] }}
              </span>
            </template>

            <!-- DATE -->
            <template v-else-if="col.type === 'date'">
              <DateInput
                v-if="row.editing"
                :name="`${col.field}-${row._rowId}`"
                v-model="row[col.field]"
                @update:modelValue="emitIfAutoSave"
              />

              <span v-else>
                {{ formatDate(row[col.field]) }}
              </span>
            </template>

            <!-- CHECKBOX -->
            <label
              v-else-if="col.type === 'checkbox'"
              class="custom-check"
              :class="{ disabled: !row.editing }"
            >
              <input
                type="checkbox"
                v-model="row[col.field]"
                :disabled="!row.editing"
                @change="emitIfAutoSave"
              />

              <span class="checkmark"></span>
            </label>

            <!-- COMBO / DROPDOWN -->
            <template v-else-if="col.type === 'combo'">
              <select
                v-if="row.editing"
                v-model="row[col.field]"
                @change="emitIfAutoSave"
              >
                <option
                  value=""
                  disabled
                >
                  {{ col.placeholder || 'Choose…' }}
                </option>

                <option
                  v-for="opt in (col.options || [])"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>

              <span v-else>
                {{ getComboLabel(col, row[col.field]) }}
              </span>
            </template>

            <!-- FALLBACK -->
            <template v-else>
              <span>
                {{ row[col.field] }}
              </span>
            </template>
          </td>

          <!-- ACTIONS -->
          <td class="align-center actions">
            <button
              v-if="!row.editing"
              type="button"
              @click="editRow(row)"
              title="Edit"
            >
              <InlineSvg
                :src="icons.edit"
                :width="20"
                :height="20"
              />
            </button>

            <button
              v-else
              type="button"
              @click="saveRow(row)"
              title="Save"
            >
              <InlineSvg
                :src="icons.save"
                :width="20"
                :height="20"
              />
            </button>

            <button
              type="button"
              class="remove-btn"
              @click="removeRow(rowIndex)"
              title="Remove"
            >
              <InlineSvg
                :src="icons.delete"
                :width="20"
                :height="20"
              />
            </button>
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr>
          <td
            :colspan="columns.length + 1"
            class="align-center empty-row"
          >
            {{ noRowsMessage }}
          </td>
        </tr>
      </tbody>

      <tfoot :class="{ alt: rows.length % 2 === 0 }">
        <tr>
          <td :colspan="columns.length + 1">
            <button
              type="button"
              class="btn btn-hyperlink"
              style="display: inline-block; margin-top: 10px;"
              @click="addRow"
            >
              {{ buttonName }}
            </button>
          </td>
        </tr>
      </tfoot>
    </table>

    <div v-else>
      <button
        type="button"
        class="btn btn-hyperlink"
        style="display: inline-block; margin-top: 10px;"
        @click="addRow"
      >
        {{ buttonName }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { icons } from '@/assets/icons'
import InlineSvg from 'vue-inline-svg'
import DateInput from '@/components/controls/DateInput.vue'

const props = defineProps({
  columns: {
    type: Array,
    required: true
  },

  modelValue: {
    type: Array,
    default: () => []
  },

  buttonName: {
    type: String,
    default: 'Add'
  },

  defaultOpen: {
    type: Boolean,
    default: false
  },

  noRowsMessage: {
    type: String,
    default: 'No rows yet'
  },

  // Opt-in only.
  // Existing EditableTable users keep the current behaviour.
  autoSaveOnChange: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([
  'update:modelValue'
])

let rowIdCounter = 1

const internalUpdate = ref(false)

const makeInternalRow = (data, editing = false) => ({
  _rowId: rowIdCounter++,
  ...data,
  editing
})

const rows = ref(
  (props.modelValue || []).map(row =>
    makeInternalRow({ ...row }, false)
  )
)

/*
 * If the parent changes modelValue externally,
 * refresh our internal rows.
 */
watch(
  () => props.modelValue,
  (next) => {
    if (internalUpdate.value) {
      return
    }

    rows.value = (next || []).map(row =>
      makeInternalRow({ ...row }, false)
    )
  },
  {
    deep: true
  }
)

/*
 * Show the table when:
 * - defaultOpen has been requested, or
 * - at least one row exists.
 */
const isOpen = computed(() =>
  props.defaultOpen || rows.value.length > 0
)

/*
 * Emit clean data back to the parent.
 *
 * Internal properties such as editing and _rowId
 * are deliberately removed.
 */
const emitClean = async () => {
  internalUpdate.value = true

  emit(
    'update:modelValue',
    rows.value.map(({ editing, _rowId, ...row }) => row)
  )

  await nextTick()

  internalUpdate.value = false
}

/*
 * Only emit immediately when autoSaveOnChange
 * has explicitly been enabled.
 */
function emitIfAutoSave() {
  if (props.autoSaveOnChange) {
    emitClean()
  }
}

/*
 * Add a new blank row.
 */
function addRow() {
  const newRowData = {}

  props.columns.forEach(col => {
    if (col.type === 'checkbox') {
      newRowData[col.field] = false
    } else {
      newRowData[col.field] = ''
    }
  })

  rows.value.push(
    makeInternalRow(newRowData, true)
  )

  /*
   * Emit immediately so the parent knows that
   * a new row exists.
   */
  emitClean()
}

/*
 * Put an existing row into edit mode.
 */
function editRow(row) {
  row.editing = true
}

/*
 * Save a row and emit its data.
 */
function saveRow(row) {
  row.editing = false
  emitClean()
}

/*
 * Remove a row.
 */
function removeRow(index) {
  rows.value.splice(index, 1)
  emitClean()
}

/*
 * Convert combo value into its display label.
 */
function getComboLabel(col, value) {
  const options = col.options || []

  const found = options.find(
    option => option.value === value
  )

  return found
    ? found.label
    : value || ''
}

/*
 * Handle number inputs without turning an
 * empty field into zero.
 */
function onNumberInput(row, field, event) {
  const rawValue = event.target.value

  row[field] =
    rawValue === ''
      ? null
      : Number(rawValue)

  if (props.autoSaveOnChange) {
    emitClean()
  }
}

/*
 * Format YYYY-MM-DD as DD/MM/YYYY for display.
 *
 * This deliberately avoids new Date(value)
 * because ISO date-only strings can introduce
 * timezone-related date changes.
 */
function formatDate(value) {
  if (!value) {
    return ''
  }

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})/
  )

  if (!match) {
    return value
  }

  const [, year, month, day] = match

  return `${day}/${month}/${year}`
}

/*
 * Decide column alignment.
 */
function getColumnAlignment(col) {
  if (
    col.type === 'text' ||
    col.type === 'combo' ||
    col.type === 'number'
  ) {
    return 'align-left'
  }

  return 'align-center'
}
</script>

<style scoped>
table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

th {
  padding: 30px;
  text-align: center;
}

td {
  padding: 30px;
  vertical-align: top;
}

.align-left {
  text-align: left;
  padding-left: 30px;
  vertical-align: top;
}

.align-center {
  text-align: center;
  vertical-align: top;
}

/*
 * Standard editable-table controls.
 */
input[type="text"],
select {
  width: 100%;
  box-sizing: border-box;
  height: 52px;
}

input {
  margin: 0;
}

select {
  padding-left: 10px;
  padding-right: 10px;
}

/*
 * Make DateInput fit naturally inside a table cell.
 *
 * :deep is needed because DateInput has its own
 * scoped styles.
 */
:deep(.date-input) {
  width: 100%;
}

:deep(.date-input input.dt) {
  width: 100%;
  max-width: 100%;
  height: 52px;
  box-sizing: border-box;
  margin: 0;
}

/*
 * Action buttons.
 */
.actions {
  white-space: nowrap;
}

.actions button {
  display: inline-block;
  margin: 0 4px;
}

tfoot {
  background-color: var(--table-row-bg-alt-colour);
}

tfoot.alt {
  background-color: rgb(254, 254, 254) !important;
}

.empty-row {
  padding: 20px;
  opacity: 0.7;
}
</style>